import { format } from "date-fns";
import * as Icons from "heroicons-react";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { Helmet } from "react-helmet";
import { Link, useNavigate } from "react-router-dom";
import { useEcomSalesByStatusQuery } from "../../../services/ecomApi";
import "./OrderReceived.css";
import { IoCopyOutline } from "react-icons/io5";
import { MdContentPaste } from "react-icons/md";
import CreateRedxOrderModal from './CreateRedxOrderModal';

const courierOptions = [
  { value: "redx", label: "Redx" },
  { value: "pathao", label: "Pathao" },
  { value: "steadfast", label: "Steadfast" },
  { value: "sundarban", label: "Sundarban" },
  { value: "ecourier", label: "eCourier" },
];

// Sample data for demo
const sampleSales = [
  {
    _id: "1",
    invoiceId: "15989",
    billerId: { name: "Hasib Shah" },
    createdAt: new Date().toISOString(),
    status: "On hold",
    grossTotalRound: 203,
    totalReceived: 203,
    changeAmount: 0,
    customerId: { phone: "01700000001" },
    totalItem: 3,
    origin: "Direct",
  },
  {
    _id: "2",
    invoiceId: "15988",
    billerId: { name: "Hasib Shah" },
    createdAt: new Date().toISOString(),
    status: "On hold",
    grossTotalRound: 299,
    totalReceived: 299,
    changeAmount: 0,
    customerId: { phone: "01700000002" },
    totalItem: 2,
    origin: "Direct",
  },
  {
    _id: "3",
    invoiceId: "15987",
    billerId: { name: "Hasib Shah" },
    createdAt: new Date().toISOString(),
    status: "Processing",
    grossTotalRound: 299,
    totalReceived: 299,
    changeAmount: 0,
    customerId: { phone: "01700000003" },
    totalItem: 4,
    origin: "Direct",
  },
  {
    _id: "4",
    invoiceId: "15941",
    billerId: { name: "Hasib Shah" },
    createdAt: new Date().toISOString(),
    status: "Processing",
    grossTotalRound: 299,
    totalReceived: 299,
    changeAmount: 0,
    customerId: { phone: "01700000004" },
    totalItem: 1,
    origin: "Direct",
  },
];

const statusColorClass = (status) => {
  switch ((status || "").toLowerCase()) {
    case "processing":
      return "status-processing";
    case "on hold":
      return "status-onhold";
    case "delivered":
      return "status-delivered";
    case "cancel":
      return "status-cancel";
    default:
      return "status-default";
  }
};

const DeliveryOrders = () => {
  const [startDate, setStartDate] = useState(format(new Date(), "MM-dd-yyyy"));
  const [endDate, setEndDate] = useState(format(new Date(), "MM-dd-yyyy"));
  const [sales, setSales] = useState([]);
  const [trackingNumbers, setTrackingNumbers] = useState({});
  const [selectedCouriers, setSelectedCouriers] = useState({});
  const [sentTracking, setSentTracking] = useState({});
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [search, setSearch] = useState("");
  const [copied, setCopied] = useState({});
  const navigate = useNavigate();
  const [showRedxModal, setShowRedxModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  const { data, isSuccess } = useEcomSalesByStatusQuery("deliver");

  useEffect(() => {
    if (data?.length > 0) {
      setSales(data);
    } else {
      setSales(sampleSales);
    }
  }, [isSuccess, data]);

  const handleTrackingChange = (id, value) => {
    setTrackingNumbers({ ...trackingNumbers, [id]: value });
  };

  const handleCourierChange = (id, value) => {
    setSelectedCouriers({ ...selectedCouriers, [id]: value });
  };

  const handleSendTracking = (id) => {
    if (!selectedCouriers[id]) {
      alert("Please select a courier.");
      return;
    }
    setSentTracking({ ...sentTracking, [id]: true });
  };

  const handleCopy = (id) => {
    if (trackingNumbers[id]) {
      navigator.clipboard.writeText(trackingNumbers[id]);
      setCopied((prev) => ({ ...prev, [id]: true }));
      setTimeout(() => setCopied((prev) => ({ ...prev, [id]: false })), 1200);
    }
  };

  const handleSelectOrder = (id) => {
    setSelectedOrders((prev) =>
      prev.includes(id) ? prev.filter((oid) => oid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedOrders(sales.map((sale) => sale._id));
    } else {
      setSelectedOrders([]);
    }
  };

  // Filtered sales by search
  const filteredSales = sales.filter((sale) =>
    sale.invoiceId?.toString().toLowerCase().includes(search.toLowerCase())
  );

  const fallbackTrackingId = "JXs324demoID487hVhe";

  const handleCreateRedxOrder = (order) => {
    setSelectedOrder(order);
    setShowRedxModal(true);
  };

  return (
    <div className="order-received-container">
      <Helmet>
        <meta charSet="utf-8" />
        <title>Aamar-Dokan-ECOM-SALE</title>
      </Helmet>
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            {/* Top controls */}
            <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
              {/* <select className="form-select w-auto">
                <option>Bulk actions</option>
                <option>Send to Courier</option>
                <option>Mark as Delivered</option>
              </select>
              <button className="btn btn-outline-secondary btn-sm">Apply</button> */}
              <DatePicker
                selected={new Date(startDate)}
                className="form-control date-input w-auto"
                onChange={(date) =>
                  setStartDate(format(new Date(date), "MM-dd-yyyy"))
                }
              />
              <input
                type="text"
                className="form-control w-auto"
                style={{ minWidth: 180 }}
                placeholder="Filter by invoice id"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            {/* Table */}
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Send To</th>
                    <th>Total</th>
                    <th>Origin</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSales.length > 0 ? (
                    filteredSales.map((sale) => (
                      <tr key={sale._id}>
                        <td>
                          <Link
                            to={`/ecom/orderinfoupdate/${sale._id}`}
                            className="invoice-id"
                          >
                            #{sale.invoiceId} {sale?.billerId?.name}
                          </Link>
                        </td>
                        <td>
                          {sale.createdAt &&
                            format(new Date(sale.createdAt), "MMM d, yyyy")}
                        </td>
                        <td>
                          <span
                            className={`status-badge ${statusColorClass(
                              sale.status
                            )}`}
                          >
                            {sale.status}
                          </span>
                        </td>
                        <td>
                          {!sentTracking[sale._id] ? (
                            <div className="d-flex align-items-center gap-2">
                              <select
                                className="form-select form-select-sm"
                                style={{ maxWidth: 120 }}
                                value={selectedCouriers[sale._id] || ""}
                                onChange={(e) =>
                                  handleCourierChange(sale._id, e.target.value)
                                }
                              >
                                <option value="">Courier</option>
                                {courierOptions.map((courier) => (
                                  <option
                                    key={courier.value}
                                    value={courier.value}
                                  >
                                    {courier.label}
                                  </option>
                                ))}
                              </select>

                              {selectedCouriers[sale._id] === 'redx' ? (
                                <button
                                  className="btn btn-dark btn-sm border-none"
                                  onClick={() => handleCreateRedxOrder(sale)}
                                  style={{background:"#F2652D", border: "none" }}
                                >
                                  Create Order
                                </button>
                              ) : (
                                <button
                                  className="btn btn-dark btn-sm border-none"
                                  onClick={() => handleSendTracking(sale._id)}
                                  style={{background:"#F2652D", border: "none" }}
                                >
                                  Send
                                </button>
                              )}
                            </div>
                          ) : (
                            <div style={{ minWidth: 200, width: 220 }}>
                              <label
                                style={{
                                  fontSize: 13,
                                  color: "#888",
                                  marginBottom: 2,
                                  display: "block",
                                }}
                              >
                                <span style={{fontWeight: 600}}>
                                  {
                                    courierOptions.find(
                                      (c) =>
                                        c.value === selectedCouriers[sale._id]
                                    )?.label
                                  } {" "}
                                </span>
                                Tracking ID
                              </label>
                              <div className="d-flex align-items-center gap-2">
                                <p style={{ margin: 0, fontFamily: 'monospace' }}>
                                  {trackingNumbers[sale?._id] || fallbackTrackingId}
                                </p>
                                <button
                                  className="btn"
                                  style={{ lineHeight: 1 }}
                                  title="Copy Tracking ID"
                                  onClick={() => {
                                    const toCopy = trackingNumbers[sale?._id] || fallbackTrackingId;
                                    navigator.clipboard.writeText(toCopy);
                                    setCopied((prev) => ({ ...prev, [sale._id]: true }));
                                    setTimeout(() => setCopied((prev) => ({ ...prev, [sale._id]: false })), 1200);
                                  }}
                                >
                                  {copied[sale._id] ? (
                                    <span
                                      style={{ color: "#28a745", fontWeight: 500, fontSize: 13 }}
                                    >
                                      <MdContentPaste size={16} />
                                    </span>
                                  ) : (
                                    <IoCopyOutline size={16} />
                                  )}
                                </button>
                              </div>
                            </div>
                          )}
                        </td>
                        <td className="amount">৳{sale.grossTotalRound}</td>
                        <td>{sale.origin || "Direct"}</td>
                        <td>
                          <button
                            className="btn p-0"
                            title="Preview Invoice"
                            onClick={() =>
                              navigate(`/ecom/invoice/${sale._id}`)
                            }
                          >
                            <Icons.EyeOutline size={22} />
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" className="no-data">
                        <div className="no-data-content">
                          <Icons.InboxOutline size={48} />
                          <p>No Orders Found</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <CreateRedxOrderModal 
        show={showRedxModal}
        handleClose={() => setShowRedxModal(false)}
        orderData={selectedOrder}
      />
    </div>
  );
};

export default DeliveryOrders;
